import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useIsFocused } from '@garnish/core';

import { useUserLocation } from '@order/shared-hooks';

import { checkIfLateOrder, checkIfPastOrder } from '../../utils';
import { useChallengeCelebration } from '../useChallengeCelebration';
import { useOrderHealthKit } from '../useOrderHealthKit';
import { useOrderStatusPolling } from '../useOrderStatusPolling';
import { useOrderStatusRequest, type UseOrderStatusRequestParams } from '../useOrderStatusRequest';

export function useOrderStatusData(params: UseOrderStatusDataParams) {
  const { isLoggedIn, customer } = params;

  const { formatMessage } = useIntl();
  const isRenderedOnScreen = useIsFocused();

  // ─── Request ─────────────────────────────────────────────────────────

  const requestParams: UseOrderStatusRequestParams = {
    isLoggedIn,
    customerId: customer.id ?? '',
  };

  const response = useOrderStatusRequest(requestParams);

  const {
    order,
    courierDetails,
    orderStatus,
    orderId,
    orderType,
    isFetching: isFetchingOrderStatus,
    refetchCanCancelStatus,
  } = response;

  // ─── Order Status Polling ────────────────────────────────────────────

  const {
    status,
    courier,
    refetchOrderStatus: refetchOrderStatusData,
  } = useOrderStatusPolling(orderId, orderType, orderStatus, courierDetails);

  const refetchOrderStatus = useCallback(() => {
    refetchCanCancelStatus();
    refetchOrderStatusData();
  }, [refetchCanCancelStatus, refetchOrderStatusData]);

  // ─── Derived Statuses ────────────────────────────────────────────────

  const isLate = checkIfLateOrder({ order, flattenedOrderStatus: status });
  const isPast = checkIfPastOrder({ order, flattenedOrderStatus: status });

  // ─── Health Kit ──────────────────────────────────────────────────────

  const { isOrderToHealthKitEnabled, orderToHealthKitLineItems } = useOrderHealthKit(order);

  // ─── User Data ───────────────────────────────────────────────────────

  const customerName = formatMessage(messages.you);
  const userLocationData = useUserLocation({
    pollingInterval: GEO_POLLING_INTERVAL,
  });
  const { geolocation } = userLocationData;
  const { latitude: userLat, longitude: userLon } = geolocation ?? {};

  // ─── Challenge Celebration ───────────────────────────────────────────

  const challengeData = useChallengeCelebration(orderId);
  const { celebration, fetching: isFetchingCelebration } = challengeData;

  // ─── Data ────────────────────────────────────────────────────────────

  return {
    ...response,
    isFetching: isFetchingOrderStatus || isFetchingCelebration,

    isRenderedOnScreen,

    status,

    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,

    isLate,
    isPast,

    customerName,
    customerLatitude: userLat,
    customerLongitude: userLon,

    courierName: courier?.name,
    courierPhone: courier?.phone,
    courierLatitude: courier?.latitude,
    courierLongitude: courier?.longitude,

    celebration,

    refetchOrderStatus,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  you: {
    defaultMessage: 'You',
    description: 'Order Status | Status Data Fetch | Customer Name',
  },
});

// ─── Constants ─────────────────────────────────────────────────────────

const GEO_POLLING_INTERVAL = 60 * 1000; // 60 seconds.

// ─── Types ───────────────────────────────────────────────────────────────────

export type UseOrderStatusDataParams = {
  isLoggedIn: boolean;
  customer: OrderStatusDataCustomer;
};

type OrderStatusDataCustomer = {
  id?: string | null;
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};
